import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import FormError from "@/components/common/FormError";
import MfaStep from "@/components/common/MfaStep";
import { toast } from "sonner";
import { ArrowLeft, Building2, Shield, Landmark, User, Briefcase, UserCog, Loader2 } from "lucide-react";

const AUTH_BASE = "https://auth.emergentagent.com";

function googleSignIn() {
  const redirect = `${window.location.origin}/auth/callback`;
  window.location.href = `${AUTH_BASE}/?redirect=${encodeURIComponent(redirect)}`;
}

const ROLE_META = {
  admin: { icon: Shield, color: "bg-blue-600", tint: "text-blue-600", hint: "admin@rajvaluation.com" },
  bank: { icon: Landmark, color: "bg-emerald-600", tint: "text-emerald-600", hint: "sbi@rajvaluation.com" },
  field: { icon: User, color: "bg-amber-600", tint: "text-amber-600", hint: "ramesh@rajvaluation.com" },
  office: { icon: Briefcase, color: "bg-violet-600", tint: "text-violet-600", hint: "priya@rajvaluation.com" },
};

export default function Login() {
  const { role } = useParams();
  const meta = ROLE_META[role] || ROLE_META.admin;
  const Icon = meta.icon;
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLang();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [mfa, setMfa] = useState(null);

  const finish = (user) => {
    if (user.role !== role) {
      toast.warning(`Signed in as ${user.role}. Redirecting to your dashboard.`);
    }
    navigate(`/${user.role}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    const res = await login(email.trim().toLowerCase(), password, remember);
    setBusy(false);
    if (!res.ok) {
      setError(res.error || "Login failed");
      return;
    }
    if (res.mfaRequired) {
      setMfa(res);
      return;
    }
    finish(res.user);
  };

  return (
    <div className="min-h-screen bg-slate-50 grid lg:grid-cols-2">
      {/* Left: photo panel (hidden mobile) */}
      <div className="hidden lg:block relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1600531529272-023c4b821f14?crop=entropy&cs=srgb&fm=jpg&q=85)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-slate-950/70 to-slate-900/60" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <Link to="/" className="flex items-center gap-2 w-fit" data-testid="back-home">
            <div className="h-9 w-9 rounded-md bg-white text-slate-950 flex items-center justify-center">
              <Building2 className="h-5 w-5" />
            </div>
            <div className="font-heading font-bold">Rana Associates</div>
          </Link>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-3">
              {t(role)}
            </p>
            <h1 className="font-heading text-4xl font-bold tracking-tight leading-tight max-w-md">
              {t("welcome_back")}
            </h1>
            <p className="mt-4 text-slate-300 max-w-md">{t("landing_sub")}</p>
          </div>
          <div className="text-xs text-slate-400">
            © {new Date().getFullYear()} Rana Associates · Pan Rajasthan
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-8"
            data-testid="link-home"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>

          <div className={`h-11 w-11 rounded-md ${meta.color} text-white flex items-center justify-center mb-6`}>
            <Icon className="h-5 w-5" />
          </div>

          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{String(role).toUpperCase()}</p>
          <h2 className="mt-2 font-heading text-3xl font-bold text-slate-950">{t(role)} {t("login")}</h2>
          <p className="mt-2 text-slate-600 text-sm">{mfa ? "One more step to keep your account safe." : "Sign in to access your role-based dashboard."}</p>

          {mfa ? (
            <MfaStep mfa={mfa} onVerified={finish} onBack={() => { setMfa(null); setPassword(""); }} />
          ) : (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">{t("email")}</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                onBlur={() => setEmail((v) => v.trim().toLowerCase())}
                autoComplete="email"
                placeholder="you@example.com"
                className="mt-1.5"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password">{t("pass_label")}</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                autoComplete="current-password"
                placeholder="••••••••"
                className="mt-1.5"
                data-testid="login-password-input"
              />
              <div className="mt-2 flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
                  <Checkbox
                    checked={remember}
                    onCheckedChange={(v) => setRemember(Boolean(v))}
                    data-testid="login-remember-me"
                  />
                  Remember me for 30 days
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-blue-600 hover:underline"
                  data-testid="login-forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
            </div>
            <FormError message={error} testId="login-error" />
            <Button
              type="submit"
              disabled={busy}
              aria-busy={busy}
              className="w-full bg-slate-950 hover:bg-slate-800 text-white h-11"
              data-testid="login-submit-button"
            >
              {busy ? (<><Loader2 className="h-4 w-4 animate-spin mr-2" /> Signing in…</>) : t("sign_in")}
            </Button>
          </form>
          )}

          {!mfa && (<>
          <div className="mt-6 relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-slate-50 px-3 text-slate-500">or</span>
            </div>
          </div>

          <button
            type="button"
            onClick={googleSignIn}
            className="mt-4 w-full h-11 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-100 inline-flex items-center justify-center gap-2 transition-colors"
            data-testid="google-signin-button"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.7 1.22 9.19 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Sign in with Google
          </button>

          <div className="mt-4 text-center text-sm text-slate-600">
            {(role === "bank" || role === "field") ? (
              <>
                Don't have an account?{" "}
                <Link to={`/signup/${role}`} className="text-blue-600 hover:underline font-medium" data-testid="login-goto-signup">
                  Sign up as {t(role)}
                </Link>
              </>
            ) : (
              <span className="text-xs text-slate-500">
                {role === "admin" ? "Admin" : "Office Staff"} accounts are created by the platform administrator.
              </span>
            )}
          </div>

          <div className="mt-8 grid grid-cols-5 gap-2">
            {["admin", "manager", "bank", "field", "office"].map((r) => (
              <Link
                key={r}
                to={`/login/${r}`}
                className={`text-center text-xs py-2 rounded-md border ${
                  r === role
                    ? "bg-slate-950 text-white border-slate-950"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
                data-testid={`switch-role-${r}`}
              >
                {t(r)}
              </Link>
            ))}
          </div>
          </>)}
        </div>
      </div>
    </div>
  );
}
